import { useAuth } from "@mindlercare/auth-ui";
import {
  type RouterOutput,
  ReactQueryV5,
} from "@mindlercare/mindlerapi-client";
const { useQuery } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import { endOfWeek, startOfWeek } from "date-fns";
import { SlotApiRes, SlotStatus } from "core/types";
import CONSTANTS from "core/constants";
import { useUserHasRole } from "hooks/userHooks";
import { useSelectedPsychologist } from "./useSelectedPsychologist";
import { useWeekDate } from "./useWeekDate";

export type RawCalendarSlot =
  RouterOutput["slots"]["psychologist"]["calendar"][number];

export const formatCalendarSlot = ({
  patientUserId,
  ...slot
}: RawCalendarSlot) => ({
  ...slot,
  status: (slot.status ?? SlotStatus.OPEN) as SlotApiRes["status"],
  feeWaived: Boolean(slot.feeWaived),
  blocked: Boolean(slot.blocked),
  userId: patientUserId,
});

const selectSlots = (slots: RawCalendarSlot[]): SlotApiRes[] =>
  slots.map(formatCalendarSlot);

export const useCalendarSlots = () => {
  const api = useMindlerApi();
  const { userId } = useAuth();
  const { date: weekDate } = useWeekDate();
  const adminSelectedPsychologist = useSelectedPsychologist();
  const isPsychologist = useUserHasRole("Psychologist");

  const psychologistUserId =
    adminSelectedPsychologist?.user.userId ?? (userId as number);

  const canFetchSlots =
    Boolean(userId) && (isPsychologist || Boolean(adminSelectedPsychologist));

  return useQuery(
    api.slots.psychologist.calendar.queryOptions(
      {
        psychologistUserId,
        startDate: startOfWeek(weekDate),
        endDate: endOfWeek(weekDate),
      },
      {
        enabled: canFetchSlots,
        refetchInterval: CONSTANTS.FETCH_INTERVAL,
        select: selectSlots,
      },
    ),
  );
};
